import React from 'react';
import {
  Button,
  Columns,
  Column,
  Heading,
  Stack,
  Text,
  TextLink,
  Box,
} from '@origin-digital/ods-core';
import { CUSTOMER_TYPE, CustomerType } from '@origin-digital/sales-common';
import { CESDialog } from './CESDialog';
import { getCesSignUpURL } from './getCesSignUpURL';

export type CESErrorAlertPresentationProps =
  | { showAsDialog?: false }
  | {
      /** Opt-in for movers-widget-wp-micro-site; other consumers retain the inline alert. */
      showAsDialog: true;
      open: boolean;
      onClose: () => void;
    };

export type CESErrorAlertProps = CESErrorAlertPresentationProps & {
  customerType: CustomerType;
  isEspec?: boolean;
  searchTerm?: string;
  /** CES site origin for consumers hosted separately from the CES app. */
  cesBaseUrl?: string;
};

export const CESErrorAlert = ({
  customerType,
  isEspec,
  searchTerm,
  cesBaseUrl,
  ...presentationProps
}: CESErrorAlertProps) => {
  const getLinkUrl = (linkCustomerType: CustomerType) =>
    getCesSignUpURL(linkCustomerType, searchTerm, isEspec, cesBaseUrl);

  if (presentationProps.showAsDialog) {
    return (
      <CESDialog
        customerType={customerType}
        open={presentationProps.open}
        onClose={presentationProps.onClose}
        href={getLinkUrl(customerType)}
      />
    );
  }

  return (
    <Box backgroundColor="transparent" data-testid="address-ces-error">
      <Columns alignX="center" collapseBelow="md" space="small">
        <Column width="2/3">
          <Stack space="large" alignX="center">
            <Stack alignX="center" space="medium">
              <img
                src="https://www.originenergy.com.au/cdn/images/originenergy/image/upload/v1726134966/spot_CES_contained-white.svg"
                alt="ces services"
                height="96px"
              />
              <Heading variant="h4" align="center">
                Great news. We offer energy services at your building.
              </Heading>
              {customerType === CUSTOMER_TYPE.RESIDENTIAL && (
                <Text align="center">
                  As you live in a unit or apartment building that is part of an embedded network,
                  we have a different offer for you. Just select your exact address in the next step
                  to get your energy service connected.
                </Text>
              )}
              {customerType === CUSTOMER_TYPE.BUSINESS && (
                <Text align="center">
                  As you run a business in a building that is part of an embedded network, we have a
                  different offer for you. Just select your exact address in the next step to get
                  your energy service connected.
                </Text>
              )}
            </Stack>
            <Button href={getLinkUrl(customerType)}>Show available energy services</Button>
            <Text align="center">
              {customerType === CUSTOMER_TYPE.RESIDENTIAL && (
                <TextLink href={getLinkUrl(CUSTOMER_TYPE.BUSINESS)}>
                  Are you a business customer?
                </TextLink>
              )}
              {customerType === CUSTOMER_TYPE.BUSINESS && (
                <TextLink href={getLinkUrl(CUSTOMER_TYPE.RESIDENTIAL)}>
                  Are you a residential customer?
                </TextLink>
              )}
            </Text>
          </Stack>
        </Column>
      </Columns>
    </Box>
  );
};
